/**
 * 3D 平台用户搜索关系图谱（three.js）
 * 结构：中心平台点 -> 所有网站用户/访客 -> 每个用户搜索过的词
 * 视觉：深蓝渐变背景、品牌蓝发光节点、边流动粒子、节点平滑过渡、呼吸脉动
 * 交互：拖拽节点（拖中心点带动全体）、悬停提示、reset() 一键恢复初始布局
 */
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js'

const TAG_TEXT = { center: '平台中心', user: '网站用户', visitor: '匿名访客', keyword: '搜索词' }

export class HotWordGraph3D {
  constructor(container) {
    if (!container) throw new Error('HotWordGraph3D: 容器不能为空')
    this.container = container
    this.disposed = false
    this._nodes = new Map()          // id -> { mesh, glow, label, cur, target, initial, size, type, name, color, appearAge, removing }
    this._edgeParticles = []         // 每条边：{ aId, bId, r, g, b, t, speed }
    this._edgeLineAttr = null
    this._edgeLineColorAttr = null
    this._particleAttr = null
    this._drag = null                // 拖拽状态
    this._dragPlane = null
    this._cameraHome = new THREE.Vector3(30, 20, 30)
    this._init()
  }

  _init() {
    const container = this.container
    container.style.position = 'relative'
    container.style.background = 'radial-gradient(circle at 50% 38%, #12306e 0%, #0b1e4d 45%, #050b22 100%)'
    const w = container.clientWidth || 800
    const h = container.clientHeight || 520

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    this.renderer.setSize(w, h)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    container.appendChild(this.renderer.domElement)

    this.labelRenderer = new CSS2DRenderer()
    this.labelRenderer.setSize(w, h)
    const labelEl = this.labelRenderer.domElement
    labelEl.style.position = 'absolute'
    labelEl.style.top = '0'
    labelEl.style.left = '0'
    labelEl.style.pointerEvents = 'none'
    container.appendChild(labelEl)

    this.scene = new THREE.Scene()

    this.camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 1000)
    this.camera.position.copy(this._cameraHome)

    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.08
    this.controls.autoRotate = false
    this.controls.minDistance = 10
    this.controls.maxDistance = 110
    this.controls.target.set(0, 0, 0)

    this.scene.add(new THREE.AmbientLight(0xcfe0ff, 0.55))
    const dir1 = new THREE.DirectionalLight(0xd6e6ff, 1.0)
    dir1.position.set(22, 32, 16)
    this.scene.add(dir1)
    const dir2 = new THREE.DirectionalLight(0xffffff, 0.35)
    dir2.position.set(-16, -8, -14)
    this.scene.add(dir2)

    this._nodeGroup = new THREE.Group()
    this._edgeGroup = new THREE.Group()
    this.scene.add(this._nodeGroup)
    this.scene.add(this._edgeGroup)

    this._glowTexture = this._createGlowTexture()

    // 悬停提示
    this._tooltip = document.createElement('div')
    this._tooltip.style.cssText =
      'position:absolute;display:none;padding:6px 10px;background:rgba(8,20,48,0.94);' +
      'border:1px solid rgba(64,169,255,0.55);border-radius:6px;font-size:13px;color:#e6f7ff;' +
      'pointer-events:none;z-index:10;white-space:nowrap;box-shadow:0 2px 14px rgba(0,0,0,0.5);'
    container.appendChild(this._tooltip)

    // 图例
    this._legend = document.createElement('div')
    this._legend.style.cssText =
      'position:absolute;top:10px;left:12px;display:flex;flex-direction:column;gap:4px;' +
      'font-size:12px;color:rgba(230,247,255,0.92);z-index:5;pointer-events:none;'
    ;[['#1890ff', '平台中心'], ['#40a9ff', '网站用户'], ['#ffc53d', '匿名访客'], ['#36cfc9', '搜索词']].forEach(([c, label]) => {
      const row = document.createElement('div')
      row.style.cssText = 'display:flex;align-items:center;gap:6px;'
      row.innerHTML = `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${c};box-shadow:0 0 8px ${c};"></span>${label}`
      this._legend.appendChild(row)
    })
    container.appendChild(this._legend)

    this._raycaster = new THREE.Raycaster()
    this._mouse = new THREE.Vector2()

    // 拖拽交互
    this._onPointerDown = this._onPointerDown.bind(this)
    this._onPointerMove = this._onPointerMove.bind(this)
    this._onPointerUp = this._onPointerUp.bind(this)
    const dom = this.renderer.domElement
    dom.addEventListener('pointerdown', this._onPointerDown)
    dom.addEventListener('pointermove', this._onPointerMove)
    dom.addEventListener('pointerup', this._onPointerUp)
    dom.addEventListener('pointercancel', this._onPointerUp)
    dom.style.cursor = 'grab'

    this._resizeObs = new ResizeObserver(() => this._onResize())
    this._resizeObs.observe(container)

    this._clock = new THREE.Clock()
    this._animate()
  }

  _createGlowTexture() {
    const size = 128
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
    g.addColorStop(0, 'rgba(255,255,255,1)')
    g.addColorStop(0.25, 'rgba(255,255,255,0.65)')
    g.addColorStop(0.6, 'rgba(255,255,255,0.2)')
    g.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, size, size)
    return new THREE.CanvasTexture(canvas)
  }

  _onResize() {
    if (this.disposed) return
    const w = this.container.clientWidth || 800
    const h = this.container.clientHeight || 520
    if (!w || !h) return
    this.camera.aspect = w / h
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(w, h)
    this.labelRenderer.setSize(w, h)
  }

  _animate() {
    if (this.disposed) return
    const dt = Math.min(this._clock.getDelta(), 0.1)
    const time = this._clock.elapsedTime
    this._updateNodes(dt, time)
    this._updateEdges(dt)
    this.controls.update()
    this.renderer.render(this.scene, this.camera)
    this.labelRenderer.render(this.scene, this.camera)
    requestAnimationFrame(() => this._animate())
  }

  /** 更新图谱；edges 用节点 id 引用（{aId, bId, color}），拖动节点时连线实时跟随 */
  updateGraph({ nodes = [], edges = [] } = {}) {
    this._rebuildEdges(edges)
    const incoming = new Set(nodes.map(n => n.id))
    for (const [id, rec] of this._nodes) {
      if (!incoming.has(id)) rec.removing = true
    }
    for (const n of nodes) {
      const rec = this._nodes.get(n.id)
      if (rec) {
        rec.target = n.pos.slice()
        rec.initial = n.pos.slice()
        rec.name = n.name
        rec.color = n.color
        rec.size = n.size
        rec.type = n.type
        if (rec.label.element) rec.label.element.textContent = n.name
      } else {
        this._addNode(n)
      }
    }
  }

  /** 一键恢复初始布局（节点 + 相机） */
  reset() {
    for (const [, rec] of this._nodes) {
      rec.target = rec.initial.slice()
    }
    this.camera.position.copy(this._cameraHome)
    this.controls.target.set(0, 0, 0)
    this.controls.update()
  }

  _rebuildEdges(edgeDescs) {
    while (this._edgeGroup.children.length) {
      const c = this._edgeGroup.children[0]
      if (c.geometry) c.geometry.dispose()
      if (c.material) c.material.dispose()
      this._edgeGroup.remove(c)
    }
    this._edgeParticles = []
    this._edgeLineAttr = null
    this._edgeLineColorAttr = null
    this._particleAttr = null
    if (!edgeDescs.length) return

    const n = edgeDescs.length

    // 实线（坐标每帧由节点位置填充）
    const linePos = new Float32Array(n * 6)
    const lineCol = new Float32Array(n * 6)
    const lineGeo = new THREE.BufferGeometry()
    this._edgeLineAttr = new THREE.BufferAttribute(linePos, 3)
    this._edgeLineColorAttr = new THREE.BufferAttribute(lineCol, 3)
    lineGeo.setAttribute('position', this._edgeLineAttr)
    lineGeo.setAttribute('color', this._edgeLineColorAttr)
    this._edgeGroup.add(new THREE.LineSegments(lineGeo, new THREE.LineBasicMaterial({
      vertexColors: true, transparent: true, opacity: 0.28
    })))

    // 流动粒子
    const pPos = new Float32Array(n * 3)
    const pCol = new Float32Array(n * 3)
    const pGeo = new THREE.BufferGeometry()
    this._particleAttr = new THREE.BufferAttribute(pPos, 3)
    pGeo.setAttribute('position', this._particleAttr)
    pGeo.setAttribute('color', new THREE.BufferAttribute(pCol, 3))
    this._edgeGroup.add(new THREE.Points(pGeo, new THREE.PointsMaterial({
      map: this._glowTexture,
      size: 0.75,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
      vertexColors: true
    })))

    edgeDescs.forEach((e, i) => {
      const c = new THREE.Color(e.color)
      this._edgeParticles.push({ aId: e.aId, bId: e.bId, r: c.r, g: c.g, b: c.b, t: (i * 0.37) % 1, speed: 0.35 + ((i * 37) % 10) / 45 })
      const co = i * 6
      lineCol[co] = c.r; lineCol[co + 1] = c.g; lineCol[co + 2] = c.b
      lineCol[co + 3] = c.r; lineCol[co + 4] = c.g; lineCol[co + 5] = c.b
      const po = i * 3
      pCol[po] = c.r; pCol[po + 1] = c.g; pCol[po + 2] = c.b
    })
  }

  _updateEdges(dt) {
    const n = this._edgeParticles.length
    if (!n) return
    const linePos = this._edgeLineAttr.array
    const pPos = this._particleAttr.array
    for (let i = 0; i < n; i++) {
      const e = this._edgeParticles[i]
      const a = this._nodes.get(e.aId)
      const b = this._nodes.get(e.bId)
      const ax = a ? a.cur[0] : 0, ay = a ? a.cur[1] : 0, az = a ? a.cur[2] : 0
      const bx = b ? b.cur[0] : 0, by = b ? b.cur[1] : 0, bz = b ? b.cur[2] : 0
      const o = i * 6
      linePos[o] = ax; linePos[o + 1] = ay; linePos[o + 2] = az
      linePos[o + 3] = bx; linePos[o + 4] = by; linePos[o + 5] = bz
      e.t += e.speed * dt
      if (e.t > 1) e.t -= 1
      const t = e.t
      const po = i * 3
      pPos[po] = ax + (bx - ax) * t
      pPos[po + 1] = ay + (by - ay) * t
      pPos[po + 2] = az + (bz - az) * t
    }
    this._edgeLineAttr.needsUpdate = true
    this._particleAttr.needsUpdate = true
  }

  _addNode(n) {
    const isKeyword = n.type === 'keyword'
    const isCenter = n.type === 'center'
    const seg = isKeyword ? 12 : 24
    const geo = new THREE.SphereGeometry(n.size, seg, seg)
    const mat = new THREE.MeshPhongMaterial({
      color: n.color,
      emissive: n.color,
      emissiveIntensity: isKeyword ? 0.55 : 0.4,
      specular: 0x888888,
      shininess: 45
    })
    const mesh = new THREE.Mesh(geo, mat)
    mesh.position.set(n.pos[0], n.pos[1], n.pos[2])
    mesh.scale.setScalar(0.2)
    this._nodeGroup.add(mesh)

    let glow = null
    if (!isKeyword) {
      const glowMat = new THREE.SpriteMaterial({
        map: this._glowTexture,
        color: n.color,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        transparent: true,
        opacity: isCenter ? 0.85 : 0.6
      })
      glow = new THREE.Sprite(glowMat)
      const glowSize = n.size * (isCenter ? 10 : 6.5)
      glow.scale.set(glowSize, glowSize, 1)
      glow.position.set(n.pos[0], n.pos[1], n.pos[2])
      this._nodeGroup.add(glow)
    }

    const div = document.createElement('div')
    div.textContent = n.name
    div.style.cssText =
      'position:absolute;font-size:' + (isCenter ? 16 : isKeyword ? 11 : 13) + 'px;' +
      (isCenter ? 'font-weight:bold;color:#fff;' : 'color:rgba(230,247,255,0.92);') +
      'line-height:1.2;text-align:center;white-space:nowrap;user-select:none;' +
      'text-shadow:0 0 6px rgba(24,144,255,0.85),0 0 2px rgba(0,0,0,0.9);'
    const label = new CSS2DObject(div)
    label.position.set(n.pos[0], n.pos[1] + n.size + 0.6, n.pos[2])
    this._nodeGroup.add(label)

    this._nodes.set(n.id, {
      mesh, glow, label,
      cur: n.pos.slice(),
      target: n.pos.slice(),
      initial: n.pos.slice(),
      size: n.size, type: n.type, name: n.name, color: n.color,
      appearAge: 0, removing: false
    })
  }

  _updateNodes(dt, time) {
    const lerp = 1 - Math.pow(0.0001, dt)
    const toRemove = []
    for (const [id, rec] of this._nodes) {
      if (rec.removing) {
        rec.appearAge += dt * 3
        const s = Math.max(0, 1 - rec.appearAge)
        rec.mesh.scale.setScalar(Math.max(0.0001, s))
        if (rec.glow) rec.glow.scale.setScalar(Math.max(0.0001, s))
        rec.label.visible = s > 0.05
        if (s <= 0.001) toRemove.push(id)
        continue
      }
      rec.cur[0] += (rec.target[0] - rec.cur[0]) * lerp
      rec.cur[1] += (rec.target[1] - rec.cur[1]) * lerp
      rec.cur[2] += (rec.target[2] - rec.cur[2]) * lerp
      rec.mesh.position.set(rec.cur[0], rec.cur[1], rec.cur[2])
      if (rec.glow) rec.glow.position.set(rec.cur[0], rec.cur[1], rec.cur[2])
      rec.label.position.set(rec.cur[0], rec.cur[1] + rec.size + 0.6, rec.cur[2])

      const pulse = 1 + Math.sin(time * 2.2 + rec.appearAge) * 0.05
      let scale = pulse
      if (rec.appearAge < 0.6) {
        rec.appearAge += dt
        const appear = Math.min(1, rec.appearAge / 0.6)
        scale *= 0.2 + 0.8 * (1 - Math.pow(1 - appear, 3))
      }
      rec.mesh.scale.setScalar(scale)
    }
    for (const id of toRemove) this._removeNodeNow(id)
  }

  _removeNodeNow(id) {
    const rec = this._nodes.get(id)
    if (!rec) return
    this._nodeGroup.remove(rec.mesh)
    if (rec.glow) this._nodeGroup.remove(rec.glow)
    this._nodeGroup.remove(rec.label)
    if (rec.mesh.geometry) rec.mesh.geometry.dispose()
    if (rec.mesh.material) rec.mesh.material.dispose()
    if (rec.glow && rec.glow.material) rec.glow.material.dispose()
    this._nodes.delete(id)
  }

  _setPointerFromEvent(e) {
    const rect = this.renderer.domElement.getBoundingClientRect()
    this._mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    this._mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
  }

  _onPointerDown(e) {
    if (e.button !== 0) return
    this._setPointerFromEvent(e)
    this._raycaster.setFromCamera(this._mouse, this.camera)
    const meshes = []
    for (const rec of this._nodes.values()) if (!rec.removing) meshes.push(rec.mesh)
    const hits = this._raycaster.intersectObjects(meshes, false)
    if (!hits.length) return
    const hitMesh = hits[0].object
    for (const [id, rec] of this._nodes) {
      if (rec.mesh === hitMesh) {
        this._drag = { nodeId: id }
        this.controls.enabled = false
        this.renderer.domElement.style.cursor = 'grabbing'
        // 拖拽平面：过节点、面向相机的平面
        const normal = new THREE.Vector3()
        this.camera.getWorldDirection(normal)
        const nodePos = new THREE.Vector3(rec.cur[0], rec.cur[1], rec.cur[2])
        this._dragPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, nodePos)
        return
      }
    }
  }

  _onPointerMove(e) {
    this._setPointerFromEvent(e)
    if (this._drag) {
      this._raycaster.setFromCamera(this._mouse, this.camera)
      const point = new THREE.Vector3()
      if (this._raycaster.ray.intersectPlane(this._dragPlane, point)) {
        const rec = this._nodes.get(this._drag.nodeId)
        if (rec) {
          const dx = point.x - rec.cur[0]
          const dy = point.y - rec.cur[1]
          const dz = point.z - rec.cur[2]
          rec.cur[0] = point.x; rec.cur[1] = point.y; rec.cur[2] = point.z
          rec.target[0] = point.x; rec.target[1] = point.y; rec.target[2] = point.z
          // 拖中心点：其它所有节点跟着平移
          if (this._drag.nodeId === 'center') {
            for (const [id, r] of this._nodes) {
              if (id === 'center') continue
              r.cur[0] += dx; r.cur[1] += dy; r.cur[2] += dz
              r.target[0] += dx; r.target[1] += dy; r.target[2] += dz
            }
          }
        }
      }
      return
    }
    // 悬停提示
    this._raycaster.setFromCamera(this._mouse, this.camera)
    const meshes = []
    for (const rec of this._nodes.values()) if (!rec.removing) meshes.push(rec.mesh)
    const hits = this._raycaster.intersectObjects(meshes, false)
    if (hits.length) {
      for (const rec of this._nodes.values()) {
        if (rec.mesh === hits[0].object) {
          this._tooltip.innerHTML = `<b>${rec.name}</b> · ${TAG_TEXT[rec.type] || ''}`
          this._tooltip.style.display = 'block'
          const rect = this.renderer.domElement.getBoundingClientRect()
          this._tooltip.style.left = (e.clientX - rect.left + 14) + 'px'
          this._tooltip.style.top = (e.clientY - rect.top + 14) + 'px'
          this.renderer.domElement.style.cursor = 'pointer'
          return
        }
      }
    }
    this._tooltip.style.display = 'none'
    this.renderer.domElement.style.cursor = 'grab'
  }

  _onPointerUp() {
    if (this._drag) {
      this._drag = null
      this._dragPlane = null
      this.controls.enabled = true
      this.renderer.domElement.style.cursor = 'grab'
    }
  }

  dispose() {
    this.disposed = true
    if (this._resizeObs) this._resizeObs.disconnect()
    const dom = this.renderer.domElement
    dom.removeEventListener('pointerdown', this._onPointerDown)
    dom.removeEventListener('pointermove', this._onPointerMove)
    dom.removeEventListener('pointerup', this._onPointerUp)
    dom.removeEventListener('pointercancel', this._onPointerUp)
    this.controls.dispose()
    for (const id of Array.from(this._nodes.keys())) this._removeNodeNow(id)
    while (this._edgeGroup.children.length) {
      const c = this._edgeGroup.children[0]
      if (c.geometry) c.geometry.dispose()
      if (c.material) c.material.dispose()
      this._edgeGroup.remove(c)
    }
    this.renderer.dispose()
    if (dom.parentNode) dom.parentNode.removeChild(dom)
    if (this.labelRenderer.domElement.parentNode) this.labelRenderer.domElement.parentNode.removeChild(this.labelRenderer.domElement)
    if (this._tooltip.parentNode) this._tooltip.parentNode.removeChild(this._tooltip)
    if (this._legend.parentNode) this._legend.parentNode.removeChild(this._legend)
  }
}
